/**
 * Get item count from table-like value
 * 
 * Handles various data structures that child tables might use:
 * - Standard array of rows: [row1, row2, ...]
 * - Object with rows/data property: { rows: [...], ... }
 * - Object with numeric keys: { 0: row1, 1: row2, ... }
 */
export function getTableItemCount(value: any): number {
  if (!value) return 0;
  
  // Standard case: Frappe-style table = array of rows
  if (Array.isArray(value)) return value.length;
  
  if (typeof value === "object") {
    // Try common shapes: { rows: [...] } or { data: [...] }
    const rows = value.rows || value.data;
    if (Array.isArray(rows)) return rows.length;
    
    // Fallback: count only "real" keys (ignore private/meta keys like __key, _idx, etc.)
    return Object.keys(value).filter((k) => !k.startsWith("_") && !k.startsWith("__")).length;
  }
  
  return 0;
}

import { formatDate, formatDateTime } from "@/lib/format";

/**
 * Format field value for standard display
 */
export function formatFieldValue(field: any, value: any): string {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  switch (field.fieldtype) {
    case "Check":
      return value === 1 || value === true ? "Yes" : "No";
    case "Date":
      return formatDate(value) || "-";
    case "Datetime":
      return formatDateTime(value) || "-";
    case "Link":
      // For Link fields, value is usually the name/id
      return value || "-";
    case "Select":
      return value || "-";
    case "Table":
    case "Table MultiSelect": {
      const count = getTableItemCount(value);
      return count === 0 ? "No items" : `${count} item${count === 1 ? "" : "s"}`;
    }
    default:
      // Defensive: avoid [object Object] for unexpected objects
      if (typeof value === "object" && value !== null) {
        return "(object)";
      }
      return String(value);
  }
}

/**
 * Formatted value that may contain HTML
 * Widgets should check for __html property and use dangerouslySetInnerHTML if present
 */
export type FormattedValue = string | { __html: string };

/**
 * Format field value for Shopify-style display
 * Returns string for normal values, or { __html: string } for HTML content
 * Widgets should check for __html property and render accordingly
 */
export function formatShopifyFieldValue(field: any, value: any, currency?: string): FormattedValue {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  switch (field.fieldtype) {
    case "Check":
      return value === 1 || value === true ? "Yes" : "No";
    case "Date":
      return formatDate(value) || "—";
    case "Datetime":
      return formatDateTime(value) || "—";
    case "Link":
      return value || "—";
    case "Select":
      return value || "—";
    case "Currency":
      // Format currency with symbol if currency provided
      const num = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(num)) return "—";
      if (currency) {
        const symbols: Record<string, string> = {
          'THB': '฿',
          'USD': '$',
          'EUR': '€',
          'GBP': '£',
        };
        const symbol = symbols[currency] || currency;
        return `${symbol}${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case "Float":
      // Format float nicely
      const floatNum = typeof value === 'string' ? parseFloat(value) : value;
      if (isNaN(floatNum)) return "—";
      return floatNum.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    case "Table":
    case "Table MultiSelect": {
      const count = getTableItemCount(value);
      return count === 0 ? "No items" : `${count} item${count === 1 ? "" : "s"}`;
    }
    case "HTML":
    case "Text Editor":
      // For HTML fields, return object with __html property
      return { __html: value };
    default:
      // Defensive: avoid [object Object] for unexpected objects
      if (typeof value === "object" && value !== null) {
        return "(object)";
      }
      // If the value contains HTML (e.g., address_display with <br>), return as HTML
      if (typeof value === "string" && value.includes("<")) {
        return { __html: value };
      }
      return String(value);
  }
}

